

export type ExternalRole = 'LANDLORD' | 'AGENT' | 'AGENCY';
export const externalRoles = {
  LANDLORD: { label: 'Landlord', title: 'Property overview', description: 'Manage your listings and follow up with prospective tenants.' },
  AGENT: { label: 'Agent', title: 'Listings & leads', description: 'Track your listings, assigned properties and new opportunities.' },
  AGENCY: { label: 'Agency', title: 'Agency overview', description: 'Manage your agency, agent roster and property portfolio.' },
} as const;
export function isExternalRole(role: string | null): role is ExternalRole {
  return role !== null && Object.hasOwn(externalRoles, role);
}
export interface DashboardNavItem {
  path: string; label: string; icon: string; module: string; action?: 'create'; referral?: boolean; matchPrefix?: boolean;
}
export interface DashboardNavGroup { label: string; items: DashboardNavItem[] }
const properties: DashboardNavGroup = { label: 'Properties', items: [
  { path: '/properties', label: 'All properties', icon: 'iconoir-home', module: 'property_management' },
  { path: '/properties/new', label: 'Add property', icon: 'iconoir-plus', module: 'property_management', action: 'create' },
  { path: '/property-verification', label: 'Property verification', icon: 'iconoir-shield-check', module: 'property_verification' },
] };
const activity: DashboardNavGroup = { label: 'Activity', items: [
  { path: '/leads', label: 'Property leads', icon: 'iconoir-group', module: 'property_leads' },
  { path: '/tenant-property-requests', label: 'Property requests', icon: 'iconoir-calendar', module: 'tenant_property_requests' },
  { path: '/conversations', label: 'Conversations', icon: 'iconoir-chat-bubble', module: 'conversations' },
] };
const account: DashboardNavGroup = { label: 'Account', items: [
  { path: '/documents', label: 'My documents', icon: 'iconoir-submit-document', module: 'documents' },
  { path: '/account/my-subscription', label: 'Subscription', icon: 'iconoir-calendar', module: 'profile' },
  { path: '/account/referrals', label: 'Referral & rewards', icon: 'iconoir-gift', module: 'profile', referral: true },
  { path: '/profile', label: 'Profile & settings', icon: 'iconoir-user-circle', module: 'profile' },
  { path: '/change-password', label: 'Security & password', icon: 'iconoir-lock', module: 'profile' },
  { path: '/kyc', label: 'KYC verification', icon: 'iconoir-shield-check', module: 'kyc_verification' },
] };
const finance: DashboardNavGroup = { label: 'Finance', items: [
  { path: '/payments', label: 'Payments', icon: 'iconoir-hand-cash', module: 'payments' },
  { path: '/invoices', label: 'Invoices', icon: 'iconoir-page', module: 'payments' },
] };
const main: DashboardNavGroup = { label: 'Workspace', items: [
  { path: '/dashboard', label: 'Overview', icon: 'iconoir-report-columns', module: 'dashboard' },
  { path: '/notifications', label: 'Notifications', icon: 'iconoir-bell', module: 'notifications' },
] };
export const dashboardNavigation: Record<ExternalRole | 'TENANT', DashboardNavGroup[]> = {
  TENANT: [
    { label: 'Activity', items: [
      { path: '/tenant/dashboard', label: 'Overview', icon: 'fa-gauge', module: 'dashboard' },
      { path: '/tenant/browse', label: 'Browse Properties', icon: 'fa-magnifying-glass', module: 'dashboard' },
      { path: '/tenant/saved', label: 'Saved Properties', icon: 'fa-heart', module: 'dashboard' },
      { path: '/tenant/enquiries', label: 'My Enquiries', icon: 'fa-envelope', module: 'dashboard', matchPrefix: true },
      { path: '/tenant/property-requests', label: 'Property Requests', icon: 'fa-calendar-check', module: 'dashboard', matchPrefix: true },
      { path: '/tenant/conversations', label: 'Conversations', icon: 'fa-comments', module: 'dashboard', matchPrefix: true },
      { path: '/tenant/notifications', label: 'Notifications', icon: 'fa-bell', module: 'dashboard' },
    ] },
    { label: 'Billing', items: [
      { path: '/tenant/subscription', label: 'Subscription', icon: 'fa-credit-card', module: 'profile' },
      { path: '/tenant/referrals', label: 'Refer & Earn', icon: 'fa-gift', module: 'profile', referral: true },
    ] },
    { label: 'Account', items: [
      { path: '/tenant/profile', label: 'My Profile', icon: 'fa-user', module: 'profile' },
      { path: '/tenant/verification', label: 'Identity Verification', icon: 'fa-shield-halved', module: 'profile' },
      { path: '/tenant/change-password', label: 'Security & Password', icon: 'fa-lock', module: 'profile' },
    ] },
  ],
  LANDLORD: [main, properties, activity, finance, account],
  AGENT: [main, { ...properties, items: properties.items.map(item => item.path === '/properties' ? { ...item, label: 'My & assigned listings' } : item) }, activity, finance, account],
  AGENCY: [main, { label: 'Agency', items: [
    { path: '/agency-profile', label: 'Agency profile', icon: 'iconoir-building', module: 'agency_management' },
    { path: '/agency-verification', label: 'Agency verification', icon: 'iconoir-shield-check', module: 'agency_management' },
    { path: '/agents-master', label: 'My agents', icon: 'iconoir-community', module: 'agents' },
  ] }, properties, activity, finance, account],
};
export function canSeeNavigation(item: DashboardNavItem, grants: Record<string, { view: boolean; create: boolean }>, referralEnabled: boolean): boolean {
  if(item.path==='/kyc' && item.module==='kyc_verification' && !item.action && !item.referral)return true;
  const grant = grants[item.module];
  return grant?.view === true && (!item.action || grant[item.action] === true) && (!item.referral || referralEnabled);
}

